/**
 * Edit Device Profile Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  EditDeviceProfileFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDeviceProfileFormData,
} from './schema'

export {
  EditDeviceProfileFormSchema,
  transformToApiRequest,
  type EditDeviceProfileFormData,
}
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { coerceFieldValue } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Layers } from 'lucide-react'

export type EditDeviceProfileComposerProps = {
  deviceProfileNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues: Partial<EditDeviceProfileFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditDeviceProfileFormData, value: unknown): string | null => {
  const shape = EditDeviceProfileFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function EditDeviceProfileComposer(props: EditDeviceProfileComposerProps) {
  const { deviceProfileNumber, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const updateProfile = useApiMutation('upsertDeviceProfile', 'POST', {
    invalidates: ['getDeviceProfiles'],
    onSuccess: () => {
      onSuccess?.()
    },
    onError: (error) => {
      onError?.(error)
    }
  })

  const form = useAppForm({
    schema: EditDeviceProfileFormSchema,
    defaultValues: {
      deviceProfileNumber: deviceProfileNumber,
      deviceProfileName: initialValues.deviceProfileName ?? '',
      deviceProfileNetwork: initialValues.deviceProfileNetwork ?? 'LoRa',
      provisioningRequired: initialValues.provisioningRequired ?? 'yes',
      region: initialValues.region ?? '',
      macVersion: initialValues.macVersion ?? '',
      supportsOtaa: initialValues.supportsOtaa ?? false,
      supportsClassB: initialValues.supportsClassB ?? false,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditDeviceProfileFormData
      const coerced = {
        deviceProfileNumber: deviceProfileNumber,
        deviceProfileName: formValue.deviceProfileName,
        deviceProfileNetwork: formValue.deviceProfileNetwork,
        provisioningRequired: formValue.provisioningRequired,
        region: formValue.region,
        macVersion: formValue.macVersion,
        supportsOtaa: formValue.supportsOtaa,
        supportsClassB: formValue.supportsClassB,
      }

      const parsed = EditDeviceProfileFormSchema.safeParse(coerced)

      if (!parsed.success) {
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await updateProfile.mutateAsync(apiRequest)
    }
  })

  const formBody = (
    <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.formGroupName} className="space-y-4">
              <div className="grid gap-4">
                {controlsByGroup.get(group.formGroupName)?.map((controlConfig) => (
                  <form.AppField
                    key={controlConfig.name}
                    name={controlConfig.name as any}
                    validators={{
                      onChange: ({ value }) =>
                        validateField(
                          controlConfig.name as keyof EditDeviceProfileFormData,
                          coerceFieldValue(controlConfig.name, value)
                        ) || undefined,
                      onBlur: ({ value }) =>
                        validateField(
                          controlConfig.name as keyof EditDeviceProfileFormData,
                          coerceFieldValue(controlConfig.name, value)
                        ) || undefined,
                    }}
                  >
                    {(bound) => {
                      const config: ControlConfig = {
                        name: controlConfig.name,
                        label: controlConfig.label,
                        placeholder: controlConfig.placeholder,
                        required: true,
                        visibility: controlConfig.visibility,
                        disabled: controlConfig.disabled,
                        type: controlConfig.inputType,
                        options: controlConfig.selectOptions as any,
                        htmlControl: controlConfig.htmlControl
                      }
                      return renderControl(config, bound)
                    }}
                  </form.AppField>
                ))}
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-4">
            {renderButtonControl({
              label: FORM_CONFIG.cancelButton?.label ?? '',
              variant: 'outline',
              onClick: onCancel,
              visibility: !!onCancel,
              type: 'button'
            })}
            {renderButtonControl(
              {
                label: updateProfile.isPending
                  ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Updating...')
                  : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Update Profile'),
                type: 'submit',
                variant: 'default'
              },
              { SubmitButton: form.SubmitButton }
            )}
          </div>
        </div>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Layers className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        formBody
      )}
    </form.AppForm>
  )
}
